import React, { useState, useEffect, useMemo } from 'react';
import { MapPin, Globe, Phone, Mail, Search, ChevronRight, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NorwayMap from '../components/NorwayMap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTIES = [
  'Agder', 'Akershus', 'Buskerud', 'Finnmark', 'Innlandet',
  'Møre og Romsdal', 'Nordland', 'Oslo', 'Rogaland',
  'Telemark', 'Troms', 'Trøndelag', 'Vestfold', 'Vestland',
  'Østfold'
];

function DealersPage() {
  const [dealers, setDealers] = useState([]);
  const [selectedCounty, setSelectedCounty] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/dealers/public`)
      .then(res => res.json())
      .then(data => { setDealers(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = selectedCounty
    ? dealers.filter(d => d.county === selectedCounty || d.is_nationwide)
    : dealers;

  // Get list of counties that have dealers
  const dealerCounties = useMemo(() => {
    const hasNationwide = dealers.some(d => d.is_nationwide);
    if (hasNationwide) return COUNTIES; // All counties active if any dealer is nationwide
    return [...new Set(dealers.map(d => d.county).filter(Boolean))];
  }, [dealers]);

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <div className="bg-emerald-900 text-white py-16 md:py-20 relative">
        <button
          onClick={() => navigate('/')}
          className="absolute top-4 left-4 sm:top-6 sm:left-6 flex items-center gap-2 text-emerald-100 hover:text-white text-sm font-medium transition-colors"
          data-testid="dealers-back-btn"
        >
          <ArrowLeft size={18} /> Tilbake til forsiden
        </button>
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="dealers-page-title">
            Finn din nærmeste forhandler
          </h1>
          <p className="text-base text-emerald-100 max-w-2xl mx-auto">
            Våre samarbeidspartnere tilbyr profesjonell oppfrisking av gravsteiner med DC-Protect
          </p>
        </div>
      </div>

      {/* County filter */}
      <div className="max-w-6xl mx-auto px-4 -mt-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col sm:flex-row items-center gap-4" data-testid="county-filter">
          <Search className="text-stone-400 hidden sm:block" size={24} />
          <select
            value={selectedCounty}
            onChange={(e) => setSelectedCounty(e.target.value)}
            className="w-full sm:flex-1 px-4 py-3 border border-stone-300 rounded-xl text-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
            data-testid="county-select"
          >
            <option value="">Alle fylker</option>
            {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {selectedCounty && (
            <button onClick={() => setSelectedCounty('')} className="text-sm text-emerald-800 hover:underline" data-testid="clear-filter">
              Vis alle
            </button>
          )}
        </div>
      </div>

      {/* Map + Dealer list */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Map column */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-stone-800 mb-4" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="map-heading">
                Klikk på et fylke
              </h2>
              <NorwayMap
                selectedCounty={selectedCounty}
                onSelectCounty={setSelectedCounty}
                dealerCounties={dealerCounties}
              />
            </div>
          </div>

          {/* Dealers column */}
          <div className="lg:col-span-3">
            {loading ? (
              <p className="text-center text-stone-500 py-16">Laster forhandlere...</p>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
                <MapPin className="mx-auto text-stone-300 mb-4" size={48} />
                <p className="text-stone-500 text-lg">
                  {selectedCounty
                    ? `Ingen forhandlere funnet i ${selectedCounty}. Vi jobber med å utvide nettverket vårt!`
                    : 'Ingen forhandlere registrert ennå.'}
                </p>
                {selectedCounty && (
                  <button onClick={() => setSelectedCounty('')} className="mt-4 text-emerald-800 hover:underline text-sm font-medium" data-testid="clear-filter-empty">
                    Vis alle forhandlere
                  </button>
                )}
              </div>
            ) : (
              <>
                <p className="text-sm text-stone-500 mb-6" data-testid="dealer-count">
                  {filtered.length} forhandler{filtered.length !== 1 ? 'e' : ''} {selectedCounty ? `i ${selectedCounty}` : 'totalt'}
                </p>
                <div className="space-y-6">
                  {filtered.map(dealer => (
                    <article
                      key={dealer.id}
                      className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-lg transition-all duration-300"
                      data-testid={`dealer-card-${dealer.id}`}
                    >
                      <div className="p-6">
                        <div className="flex items-start gap-4">
                          {dealer.logo_url ? (
                            <img src={dealer.logo_url} alt={dealer.company_name} className="w-16 h-16 rounded-xl object-contain bg-stone-50 border border-stone-100 p-1 flex-shrink-0" />
                          ) : (
                            <div className="w-16 h-16 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-2xl font-bold text-emerald-800">{dealer.company_name.charAt(0)}</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h2 className="text-xl font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                              {dealer.company_name}
                            </h2>
                            {dealer.county && (
                              <div className="flex items-center gap-1 text-sm text-emerald-800">
                                <MapPin size={14} />
                                <span>{dealer.is_nationwide ? 'Hele landet' : dealer.county}</span>
                              </div>
                            )}
                            {dealer.is_nationwide && (
                              <span className="inline-block mt-1 text-xs font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full" data-testid={`nationwide-badge-${dealer.id}`}>
                                Landsdekkende
                              </span>
                            )}
                          </div>
                        </div>

                        {dealer.description && (
                          <p className="text-stone-600 text-sm mt-4 leading-relaxed">{dealer.description}</p>
                        )}

                        <div className="mt-4 space-y-2">
                          {dealer.address && (
                            <div className="flex items-center gap-2 text-sm text-stone-500">
                              <MapPin size={14} className="flex-shrink-0" />
                              <span>{dealer.address}</span>
                            </div>
                          )}
                          {(dealer.public_phone || dealer.phone) && (
                            <a href={`tel:${dealer.public_phone || dealer.phone}`} className="flex items-center gap-2 text-sm text-stone-600 hover:text-emerald-800 transition-colors">
                              <Phone size={14} className="flex-shrink-0" />
                              <span>{dealer.public_phone || dealer.phone}</span>
                            </a>
                          )}
                          {(dealer.public_email || dealer.email) && (
                            <a href={`mailto:${dealer.public_email || dealer.email}`} className="flex items-center gap-2 text-sm text-stone-600 hover:text-emerald-800 transition-colors">
                              <Mail size={14} className="flex-shrink-0" />
                              <span>{dealer.public_email || dealer.email}</span>
                            </a>
                          )}
                        </div>

                        {dealer.website_url && (
                          <a
                            href={dealer.website_url.startsWith('http') ? dealer.website_url : `https://${dealer.website_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 mt-4 bg-emerald-800 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-900 transition-colors"
                            data-testid={`dealer-website-${dealer.id}`}
                          >
                            <Globe size={14} />
                            Besok nettsiden
                            <ChevronRight size={14} />
                          </a>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-stone-100 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Bli forhandler?
          </h2>
          <p className="text-stone-600 mb-6">
            Er du interessert i a tilby DC-Protect til dine kunder? Ta kontakt med oss for mer informasjon.
          </p>
          <button
            onClick={() => navigate('/#kontakt')}
            className="bg-emerald-800 text-white px-8 py-3 rounded-xl font-medium hover:bg-emerald-900 transition-colors"
            data-testid="become-dealer-btn"
          >
            Kontakt oss
          </button>
        </div>
      </div>
    </div>
  );
}

export default DealersPage;
